import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { 
  X, Settings, KeyRound, Sliders, Volume2, VolumeX, MapPin, 
  Database, Download, Upload, Trash2, Check, Sparkles, ShieldCheck, 
  Cpu, Globe, Sun, Moon, RefreshCw, CheckCircle2, AlertTriangle,
  Radio, Layers, Save, Server, Eye, EyeOff, Search, Terminal,
  Zap, Gauge, Wifi, HardDrive, Palette, ExternalLink, Activity
} from "lucide-react";
import { toast } from "sonner";
import { usePrometheusVoice } from "@/hooks/usePrometheusVoice";
import { useLanguage } from "@/hooks/useLanguage";
import { ConfirmDialog } from "./ConfirmDialog";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface PRMunicipalityData {
  id: string;
  name: string;
  region: string;
  hsp: number;
  windMph: number;
  humidityAvg: number;
}

export const PR_ALL_78_MUNICIPALITIES: PRMunicipalityData[] = [
  { id: "adjuntas", name: "Adjuntas", region: "Cordillera Central / Montaña", hsp: 4.7, windMph: 165, humidityAvg: 72 },
  { id: "aguada", name: "Aguada", region: "Costa Noroeste", hsp: 5.3, windMph: 170, humidityAvg: 75 },
  { id: "aguadilla", name: "Aguadilla", region: "Costa Noroeste", hsp: 5.4, windMph: 170, humidityAvg: 74 },
  { id: "aguas_buenas", name: "Aguas Buenas", region: "Sierra Central", hsp: 4.8, windMph: 170, humidityAvg: 78 },
  { id: "aibonito", name: "Aibonito", region: "Montaña / La Fría", hsp: 4.8, windMph: 165, humidityAvg: 70 },
  { id: "anasco", name: "Añasco", region: "Costa Oeste", hsp: 5.2, windMph: 170, humidityAvg: 76 },
  { id: "arecibo", name: "Arecibo", region: "Costa Norte", hsp: 5.3, windMph: 175, humidityAvg: 77 },
  { id: "arroyo", name: "Arroyo", region: "Costa Sur", hsp: 5.6, windMph: 170, humidityAvg: 68 },
  { id: "barceloneta", name: "Barceloneta", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 77 },
  { id: "barranquitas", name: "Barranquitas", region: "Cordillera Central", hsp: 4.7, windMph: 165, humidityAvg: 73 },
  { id: "bayamon", name: "Bayamón", region: "Área Metro Norte", hsp: 5.1, windMph: 175, humidityAvg: 78 },
  { id: "cabo_rojo", name: "Cabo Rojo", region: "Costa Suroeste", hsp: 5.8, windMph: 170, humidityAvg: 64 },
  { id: "caguas", name: "Caguas", region: "Valle del Turabo", hsp: 4.9, windMph: 170, humidityAvg: 80 },
  { id: "camuy", name: "Camuy", region: "Costa Norte", hsp: 5.3, windMph: 175, humidityAvg: 76 },
  { id: "canovanas", name: "Canóvanas", region: "Región Noreste", hsp: 5.2, windMph: 175, humidityAvg: 80 },
  { id: "carolina", name: "Carolina", region: "Área Metro Este", hsp: 5.2, windMph: 175, humidityAvg: 78 },
  { id: "catano", name: "Cataño", region: "Bahía de San Juan", hsp: 5.1, windMph: 175, humidityAvg: 80 },
  { id: "cayey", name: "Cayey", region: "Sierra de Cayey", hsp: 4.8, windMph: 165, humidityAvg: 74 },
  { id: "ceiba", name: "Ceiba", region: "Costa Este / Salitre", hsp: 5.5, windMph: 180, humidityAvg: 81 },
  { id: "ciales", name: "Ciales", region: "Zona Montañosa", hsp: 4.7, windMph: 165, humidityAvg: 76 },
  { id: "cidra", name: "Cidra", region: "Valle / Montaña", hsp: 4.8, windMph: 165, humidityAvg: 76 },
  { id: "coamo", name: "Coamo", region: "Región Sur Seca", hsp: 5.5, windMph: 170, humidityAvg: 66 },
  { id: "comerio", name: "Comerío", region: "Cordillera Central", hsp: 4.7, windMph: 165, humidityAvg: 76 },
  { id: "corozal", name: "Corozal", region: "Zona Central Norte", hsp: 4.8, windMph: 170, humidityAvg: 78 },
  { id: "culebra", name: "Culebra", region: "Isla Municipio", hsp: 6.0, windMph: 185, humidityAvg: 79 },
  { id: "dorado", name: "Dorado", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 79 },
  { id: "fajardo", name: "Fajardo", region: "Costa Este / Salitre", hsp: 5.5, windMph: 180, humidityAvg: 82 },
  { id: "florida", name: "Florida", region: "Zona Kárstica Norte", hsp: 5.0, windMph: 170, humidityAvg: 77 },
  { id: "guanica", name: "Guánica", region: "Costa Suroeste Seca", hsp: 5.8, windMph: 170, humidityAvg: 60 },
  { id: "guayama", name: "Guayama", region: "Costa Sur", hsp: 5.6, windMph: 170, humidityAvg: 68 },
  { id: "guayanilla", name: "Guayanilla", region: "Costa Suroeste", hsp: 5.6, windMph: 170, humidityAvg: 65 },
  { id: "guaynabo", name: "Guaynabo", region: "Área Metro", hsp: 5.1, windMph: 175, humidityAvg: 77 },
  { id: "gurabo", name: "Gurabo", region: "Valle del Turabo", hsp: 5.0, windMph: 170, humidityAvg: 79 },
  { id: "hatillo", name: "Hatillo", region: "Costa Norte Ganadera", hsp: 5.3, windMph: 175, humidityAvg: 76 },
  { id: "hormigueros", name: "Hormigueros", region: "Región Oeste", hsp: 5.2, windMph: 170, humidityAvg: 74 },
  { id: "humacao", name: "Humacao", region: "Costa Sureste", hsp: 5.3, windMph: 175, humidityAvg: 80 },
  { id: "isabela", name: "Isabela", region: "Costa Noroeste", hsp: 5.4, windMph: 175, humidityAvg: 75 },
  { id: "jayuya", name: "Jayuya", region: "Cordillera / Cerro Punta", hsp: 4.6, windMph: 165, humidityAvg: 71 },
  { id: "juana_diaz", name: "Juana Díaz", region: "Costa Sur", hsp: 5.6, windMph: 170, humidityAvg: 65 },
  { id: "juncos", name: "Juncos", region: "Región Este", hsp: 5.0, windMph: 170, humidityAvg: 79 },
  { id: "lajas", name: "Lajas", region: "Costa Suroeste", hsp: 5.7, windMph: 170, humidityAvg: 63 },
  { id: "lares", name: "Lares", region: "Zona Montañosa Oeste", hsp: 4.8, windMph: 165, humidityAvg: 74 },
  { id: "las_marias", name: "Las Marías", region: "Cordillera Oeste", hsp: 4.8, windMph: 165, humidityAvg: 74 },
  { id: "las_piedras", name: "Las Piedras", region: "Región Este", hsp: 5.1, windMph: 175, humidityAvg: 81 },
  { id: "loiza", name: "Loíza", region: "Costa Noreste / Salitre", hsp: 5.3, windMph: 175, humidityAvg: 83 },
  { id: "luquillo", name: "Luquillo", region: "Costa Noreste", hsp: 5.4, windMph: 180, humidityAvg: 82 },
  { id: "manati", name: "Manatí", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 78 },
  { id: "maricao", name: "Maricao", region: "Bosque / Montaña Oeste", hsp: 4.7, windMph: 165, humidityAvg: 73 },
  { id: "maunabo", name: "Maunabo", region: "Costa Sureste", hsp: 5.4, windMph: 175, humidityAvg: 81 },
  { id: "mayaguez", name: "Mayagüez", region: "Costa Oeste / Sultana", hsp: 5.1, windMph: 170, humidityAvg: 76 },
  { id: "moca", name: "Moca", region: "Interior Noroeste", hsp: 5.2, windMph: 170, humidityAvg: 76 },
  { id: "morovis", name: "Morovis", region: "Zona Central Norte", hsp: 4.8, windMph: 170, humidityAvg: 77 },
  { id: "naguabo", name: "Naguabo", region: "Costa Este", hsp: 5.3, windMph: 180, humidityAvg: 82 },
  { id: "naranjito", name: "Naranjito", region: "Región Central", hsp: 4.8, windMph: 170, humidityAvg: 78 },
  { id: "orocovis", name: "Orocovis", region: "Centro Geográfico PR", hsp: 4.7, windMph: 165, humidityAvg: 73 },
  { id: "patillas", name: "Patillas", region: "Costa Sur / Esmeralda", hsp: 5.5, windMph: 170, humidityAvg: 73 },
  { id: "penuelas", name: "Peñuelas", region: "Costa Sur", hsp: 5.6, windMph: 170, humidityAvg: 66 },
  { id: "ponce", name: "Ponce", region: "Costa Sur Señorial", hsp: 5.7, windMph: 170, humidityAvg: 62 },
  { id: "quebradillas", name: "Quebradillas", region: "Costa Noroeste", hsp: 5.3, windMph: 175, humidityAvg: 76 },
  { id: "rincon", name: "Rincón", region: "Costa Oeste / Surf", hsp: 5.4, windMph: 170, humidityAvg: 74 },
  { id: "rio_grande", name: "Río Grande", region: "Falda de El Yunque", hsp: 5.2, windMph: 180, humidityAvg: 82 },
  { id: "sabana_grande", name: "Sabana Grande", region: "Suroeste", hsp: 5.5, windMph: 170, humidityAvg: 67 },
  { id: "salinas", name: "Salinas", region: "Costa Sur Seca", hsp: 5.7, windMph: 170, humidityAvg: 62 },
  { id: "san_german", name: "San Germán", region: "Suroeste Colonial", hsp: 5.4, windMph: 170, humidityAvg: 69 },
  { id: "san_juan", name: "San Juan", region: "Área Metro Capital", hsp: 5.2, windMph: 175, humidityAvg: 78 },
  { id: "san_lorenzo", name: "San Lorenzo", region: "Valle del Este", hsp: 5.0, windMph: 170, humidityAvg: 80 },
  { id: "san_sebastian", name: "San Sebastián", region: "Noroeste Interior", hsp: 5.0, windMph: 165, humidityAvg: 75 },
  { id: "santa_isabel", name: "Santa Isabel", region: "Costa Sur / Vientos", hsp: 5.8, windMph: 170, humidityAvg: 60 },
  { id: "toa_alta", name: "Toa Alta", region: "Norte Central", hsp: 5.1, windMph: 175, humidityAvg: 78 },
  { id: "toa_baja", name: "Toa Baja", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 79 },
  { id: "trujillo_alto", name: "Trujillo Alto", region: "Área Metro Este", hsp: 5.1, windMph: 175, humidityAvg: 79 },
  { id: "utuado", name: "Utuado", region: "Montaña Central", hsp: 4.7, windMph: 165, humidityAvg: 75 },
  { id: "vega_alta", name: "Vega Alta", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 78 },
  { id: "vega_baja", name: "Vega Baja", region: "Costa Norte", hsp: 5.2, windMph: 175, humidityAvg: 78 },
  { id: "vieques", name: "Vieques", region: "Isla Municipio Nena", hsp: 5.9, windMph: 185, humidityAvg: 80 },
  { id: "villalba", name: "Villalba", region: "Montaña / Vistas Sur", hsp: 4.8, windMph: 165, humidityAvg: 71 },
  { id: "yabucoa", name: "Yabucoa", region: "Valle Costero Sureste", hsp: 5.3, windMph: 175, humidityAvg: 81 },
  { id: "yauco", name: "Yauco", region: "Suroeste del Café", hsp: 5.5, windMph: 170, humidityAvg: 66 }
];

type SettingsTab = "ai" | "hardware" | "pr_location" | "voice" | "appearance" | "backups";

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("ai");
  const [settingsSearch, setSettingsSearch] = useState("");

  // AI & API Keys
  const [googleKey, setGoogleKey] = useState("");
  const [openaiKey, setOpenaiKey] = useState("");
  const [anthropicKey, setAnthropicKey] = useState("");
  const [nvidiaKey, setNvidiaKey] = useState("");
  const [showKeys, setShowKeys] = useState({ google: false, openai: false, anthropic: false, nvidia: false });
  const [defaultModel, setDefaultModel] = useState("gemini_38_flash");
  const [imageModel, setImageModel] = useState("gpt-image-2.5-flare");
  const [backendUrl, setBackendUrl] = useState("http://localhost:8080");
  const [backendStatus, setBackendStatus] = useState<"online" | "offline" | "checking">("checking");
  const [latencyTestResult, setLatencyTestResult] = useState<string | null>(null);
  const [isTestingLatency, setIsTestingLatency] = useState(false);

  // Hardware & Web Serial
  const [baudRate, setBaudRate] = useState(115200);
  const [autoScrollSerial, setAutoScrollSerial] = useState(true);
  const [serialTimestamps, setSerialTimestamps] = useState(true);
  const [mcpPort, setMcpPort] = useState(8080);
  const [hasWebSerialSupport, setHasWebSerialSupport] = useState(false);

  // Puerto Rico Context & Energy
  const [selectedMunicipality, setSelectedMunicipality] = useState("san_juan");
  const [municipalityQuery, setMunicipalityQuery] = useState("");
  const [roofType, setRoofType] = useState<"concreto" | "galvanizado" | "madera">("concreto");
  const [lumaRate, setLumaRate] = useState(0.28);
  const [systemVoltage, setSystemVoltage] = useState<12 | 24 | 48>(48);
  const [batteryAutonomyH, setBatteryAutonomyH] = useState(24);

  // Appearance & Theme
  const { lang, toggleLang } = useLanguage();
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [blueprintOpacity, setBlueprintOpacity] = useState<"normal" | "subtle" | "off">("normal");

  // Storage Stats
  const [storageUsageBytes, setStorageUsageBytes] = useState(0);

  // Backup & Reset state
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Voice Engine hook
  const {
    spanishVoices,
    englishVoices,
    selectedVoice,
    config: voiceConfig,
    updateConfig: updateVoiceConfig,
    selectVoiceByURI,
    speak
  } = usePrometheusVoice();

  const availableVoiceList = (voiceConfig?.languageMode === "en-us" ? (englishVoices || []) : (spanishVoices || [])) || [];

  // Load stored settings on open
  useEffect(() => {
    if (!isOpen) return;

    setGoogleKey(localStorage.getItem("PROMETHEUS_GOOGLE_API_KEY") || "");
    setOpenaiKey(localStorage.getItem("PROMETHEUS_OPENAI_API_KEY") || "");
    setAnthropicKey(localStorage.getItem("PROMETHEUS_ANTHROPIC_API_KEY") || "");
    setNvidiaKey(localStorage.getItem("PROMETHEUS_NVIDIA_API_KEY") || "");
    setDefaultModel(localStorage.getItem("PROMETHEUS_DEFAULT_MODEL") || "gemini_38_flash");
    setImageModel(localStorage.getItem("PROMETHEUS_IMAGE_MODEL") || "gpt-image-2.5-flare");
    setBackendUrl(localStorage.getItem("PROMETHEUS_BACKEND_URL") || "http://localhost:8080");

    // Hardware
    setBaudRate(parseInt(localStorage.getItem("prometheus_serial_baud") || "115200", 10));
    setAutoScrollSerial(localStorage.getItem("prometheus_serial_autoscroll") !== "false");
    setSerialTimestamps(localStorage.getItem("prometheus_serial_timestamps") !== "false");
    setMcpPort(parseInt(localStorage.getItem("prometheus_mcp_port") || "8080", 10));

    // PR Settings
    setSelectedMunicipality(localStorage.getItem("prometheus_pr_municipality") || "san_juan");
    setRoofType((localStorage.getItem("prometheus_roof_type") as any) || "concreto");
    setLumaRate(parseFloat(localStorage.getItem("prometheus_luma_kwh_rate") || "0.28"));
    setSystemVoltage(parseInt(localStorage.getItem("prometheus_system_voltage") || "48", 10) as any);
    setBatteryAutonomyH(parseInt(localStorage.getItem("prometheus_battery_autonomy_h") || "24", 10));

    // Theme & Blueprint
    const savedTheme = (localStorage.getItem("prometheus_theme") as "dark" | "light") || "dark";
    setTheme(savedTheme);
    setBlueprintOpacity((localStorage.getItem("prometheus_blueprint_opacity") as any) || "normal");

    // Browser Web Serial check
    setHasWebSerialSupport(typeof navigator !== "undefined" && "serial" in navigator);

    // Compute storage bytes
    let totalBytes = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) totalBytes += k.length + (localStorage.getItem(k)?.length || 0);
      }
    } catch {}
    setStorageUsageBytes(totalBytes);

    checkBackendHealth();
  }, [isOpen]);

  const checkBackendHealth = async () => {
    setBackendStatus("checking");
    try {
      const url = localStorage.getItem("PROMETHEUS_BACKEND_URL") || "http://localhost:8080";
      const res = await fetch(`${url}/api/notifications`, { signal: AbortSignal.timeout(2500) });
      setBackendStatus(res.ok ? "online" : "offline");
    } catch {
      setBackendStatus("offline");
    }
  };

  const testApiLatency = async () => {
    setIsTestingLatency(true);
    setLatencyTestResult(null);
    const start = performance.now();
    try {
      // Test Google Gemini if key available, otherwise backend
      if (googleKey.trim()) {
        const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${googleKey.trim()}`, {
          method: "GET",
          signal: AbortSignal.timeout(4000)
        });
        const elapsed = Math.round(performance.now() - start);
        if (res.ok) {
          setLatencyTestResult(`Google Gemini API: ${elapsed}ms (Operacional ✓)`);
          toast.success(`⚡ Conexión verificada: ${elapsed}ms`);
        } else {
          setLatencyTestResult(`Respuesta HTTP ${res.status}: Clave no autorizada`);
          toast.error(`Error en validación de API Key (${res.status})`);
        }
      } else {
        // Fallback test to local backend
        const res = await fetch(`${backendUrl}/api/notifications`, { signal: AbortSignal.timeout(3000) });
        const elapsed = Math.round(performance.now() - start);
        setLatencyTestResult(`Jarvis Backend Local: ${elapsed}ms (${res.ok ? "Activo ✓" : "Offline"})`);
      }
    } catch (err: any) {
      setLatencyTestResult(`Error de tiempo de espera / sin conexión`);
    } finally {
      setIsTestingLatency(false);
    }
  };

  const requestSerialPermissionTest = async () => {
    if (!hasWebSerialSupport) {
      toast.error("Tu navegador no soporta la Web Serial API. Usa Google Chrome, Microsoft Edge u Opera.");
      return;
    }
    try {
      const port = await (navigator as any).serial.requestPort();
      const info = port.getInfo ? port.getInfo() : {};
      toast.success(`🔌 Puerto serial detectado: USB VID: ${info.usbVendorId || "0x10C4"} PID: ${info.usbProductId || "0xEA60"}`);
    } catch {
      toast.info("Selección de puerto serial cancelada");
    }
  };

  const handleSaveAll = () => {
    // 1. AI
    localStorage.setItem("PROMETHEUS_GOOGLE_API_KEY", googleKey.trim());
    localStorage.setItem("PROMETHEUS_OPENAI_API_KEY", openaiKey.trim());
    localStorage.setItem("PROMETHEUS_ANTHROPIC_API_KEY", anthropicKey.trim());
    localStorage.setItem("PROMETHEUS_NVIDIA_API_KEY", nvidiaKey.trim());
    localStorage.setItem("PROMETHEUS_DEFAULT_MODEL", defaultModel);
    localStorage.setItem("PROMETHEUS_IMAGE_MODEL", imageModel);
    localStorage.setItem("PROMETHEUS_BACKEND_URL", backendUrl.trim());

    // 2. Hardware
    localStorage.setItem("prometheus_serial_baud", baudRate.toString());
    localStorage.setItem("prometheus_serial_autoscroll", autoScrollSerial ? "true" : "false");
    localStorage.setItem("prometheus_serial_timestamps", serialTimestamps ? "true" : "false");
    localStorage.setItem("prometheus_mcp_port", mcpPort.toString());

    // 3. PR Context
    localStorage.setItem("prometheus_pr_municipality", selectedMunicipality);
    localStorage.setItem("prometheus_roof_type", roofType);
    localStorage.setItem("prometheus_luma_kwh_rate", lumaRate.toString());
    localStorage.setItem("prometheus_system_voltage", systemVoltage.toString());
    localStorage.setItem("prometheus_battery_autonomy_h", batteryAutonomyH.toString());

    // 4. Appearance
    localStorage.setItem("prometheus_theme", theme);
    localStorage.setItem("prometheus_blueprint_opacity", blueprintOpacity);

    // Dispatch global event for live reactivity across open components
    window.dispatchEvent(new CustomEvent("prometheus_settings_updated", {
      detail: { municipality: selectedMunicipality, lumaRate, systemVoltage, theme }
    }));

    toast.success("✅ Ajustes del laboratorio guardados y aplicados.");
    onClose();
  };

  const handleToggleTheme = (newTheme: "dark" | "light") => {
    setTheme(newTheme);
    localStorage.setItem("prometheus_theme", newTheme);
    if (newTheme === "light") {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    }
  };

  // Export Full Platform Backup (.json)
  const handleExportBackup = () => {
    const backupData: Record<string, any> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("prometheus") || key.startsWith("PROMETHEUS"))) {
        try {
          backupData[key] = JSON.parse(localStorage.getItem(key) || "");
        } catch {
          backupData[key] = localStorage.getItem(key);
        }
      }
    }

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(backupData, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `prometheus_backup_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toast.success("📦 Respaldo descargado exitosamente.");
  };

  // Import Backup (.json)
  const handleImportBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        Object.keys(json).forEach((key) => {
          const val = typeof json[key] === "object" ? JSON.stringify(json[key]) : json[key];
          localStorage.setItem(key, val);
        });
        toast.success("✅ Respaldo restaurado con éxito. Aplicando parámetros...");
        setTimeout(() => window.location.reload(), 1000);
      } catch (err) {
        toast.error("Error al leer el archivo JSON de respaldo.");
      }
    };
    reader.readAsText(file);
  };

  // Factory Reset
  const handleFactoryReset = () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("prometheus") || key.startsWith("PROMETHEUS"))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    setResetConfirmOpen(false);
    toast.success("🧹 Plataforma restablecida a valores de fábrica.");
    setTimeout(() => window.location.reload(), 800);
  };

  // Filter municipalities
  const filteredMunicipalities = useMemo(() => {
    const q = municipalityQuery.toLowerCase().trim();
    if (!q) return PR_ALL_78_MUNICIPALITIES;
    return PR_ALL_78_MUNICIPALITIES.filter((m) => 
      m.name.toLowerCase().includes(q) || m.region.toLowerCase().includes(q)
    );
  }, [municipalityQuery]);

  const currentMun = useMemo(() => {
    return PR_ALL_78_MUNICIPALITIES.find((m) => m.id === selectedMunicipality) || PR_ALL_78_MUNICIPALITIES[0];
  }, [selectedMunicipality]);

  if (!isOpen) return null;

  const tabsConfig = [
    { id: "ai" as const, label: "IA & Modelos", icon: Sparkles, badge: googleKey ? "Listo" : "Configurar" },
    { id: "hardware" as const, label: "Hardware & Serial", icon: Cpu, badge: hasWebSerialSupport ? "USB OK" : "No Serial" },
    { id: "pr_location" as const, label: "Puerto Rico & Clima", icon: MapPin, badge: currentMun.name },
    { id: "voice" as const, label: "Audio & Voz", icon: Volume2, badge: voiceConfig?.provider === "openai-hd" ? "HD" : "Neural" },
    { id: "appearance" as const, label: "Apariencia & Taller", icon: Palette, badge: theme === "dark" ? "Oscuro" : "Claro" },
    { id: "backups" as const, label: "Memoria & Respaldos", icon: Database, badge: `${Math.round(storageUsageBytes / 1024)} KB` },
  ];

  // Filter tabs based on search input
  const filteredTabs = tabsConfig.filter(t => 
    t.label.toLowerCase().includes(settingsSearch.toLowerCase()) ||
    t.badge.toLowerCase().includes(settingsSearch.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-hidden animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col h-[85vh] max-h-[760px] font-mono-tech animate-in zoom-in-95 duration-200">
        
        {/* Top Header */}
        <div className="p-4 sm:p-5 border-b border-border bg-muted/20 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shadow-xs">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-serif-display text-base sm:text-lg font-bold text-foreground">
                Configuración & Calibración del Lab
              </h3>
              <p className="text-[9.5px] text-muted-foreground">
                Prometheus Lab AI · Motor de Inteligencia, Hardware Serial, Clima PR y Acústica
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAll}
              className="px-3.5 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-xl shadow-xs hover:bg-primary/90 transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Guardar</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 2-Column Split: Left Sidebar + Right Content Area */}
        <div className="flex flex-1 overflow-hidden">
          
          {/* Left Navigation Sidebar */}
          <aside className="w-64 border-r border-border bg-muted/10 p-3 flex flex-col justify-between shrink-0 overflow-y-auto hidden md:flex">
            <div className="space-y-3">
              {/* Settings Search Filter */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={settingsSearch}
                  onChange={(e) => setSettingsSearch(e.target.value)}
                  placeholder="Buscar ajustes..."
                  className="w-full bg-background border border-border pl-8 pr-2.5 py-1.5 rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary font-mono-tech"
                />
              </div>

              {/* Navigation Items */}
              <nav className="space-y-1">
                {filteredTabs.map((tab) => {
                  const IconComp = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <IconComp className={`w-4 h-4 ${isActive ? "text-primary-foreground" : "text-primary"}`} />
                        <span>{tab.label}</span>
                      </div>
                      <span className={`text-[8.5px] px-1.5 py-0.5 rounded uppercase font-semibold ${
                        isActive 
                          ? "bg-black/20 text-white" 
                          : "bg-muted border border-border text-muted-foreground"
                      }`}>
                        {tab.badge}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Sidebar Bottom Status */}
            <div className="p-3 bg-card border border-border rounded-xl space-y-1.5 text-[9.5px]">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Server className="w-3 h-3 text-primary" /> Backend Jarvis:
                </span>
                <span className={`font-bold ${backendStatus === "online" ? "text-emerald-500" : "text-amber-500"}`}>
                  {backendStatus === "online" ? "En Línea ✓" : "Offline"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Cpu className="w-3 h-3 text-emerald-500" /> Web Serial:
                </span>
                <span className={`font-bold ${hasWebSerialSupport ? "text-emerald-500" : "text-muted-foreground"}`}>
                  {hasWebSerialSupport ? "Nativo ✓" : "No Soportado"}
                </span>
              </div>
            </div>
          </aside>

          {/* Mobile Tab Bar */}
          <div className="md:hidden border-b border-border bg-muted/20 p-1.5 flex gap-1 overflow-x-auto shrink-0">
            {tabsConfig.map((tab) => {
              const IconComp = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                    isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <IconComp className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right Content Panel */}
          <main className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6 text-xs bg-card">
            
            {/* ================= TAB 1: AI & API KEYS ================= */}
            {activeTab === "ai" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                {/* Status & Latency Card */}
                <div className="p-4 rounded-xl bg-muted/20 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                      <Server className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                        Servidor Jarvis Backend & Gateway
                      </span>
                      <span className="text-foreground font-mono font-bold text-xs">{backendUrl}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={testApiLatency}
                      disabled={isTestingLatency}
                      className="px-3 py-1.5 rounded-lg bg-card hover:bg-muted border border-border text-foreground font-bold text-[10px] flex items-center gap-1.5 cursor-pointer shadow-xs"
                    >
                      <Activity className={`w-3 h-3 text-primary ${isTestingLatency ? "animate-spin" : ""}`} />
                      <span>{isTestingLatency ? "Midiendo..." : "Probar Latencia"}</span>
                    </button>
                    <button
                      onClick={checkBackendHealth}
                      className="p-1.5 rounded-lg bg-card hover:bg-muted border border-border text-foreground cursor-pointer shadow-xs"
                      title="Verificar conexión de backend"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {latencyTestResult && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl text-[10.5px] text-primary font-mono flex items-center gap-2 animate-in fade-in">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <span>{latencyTestResult}</span>
                  </div>
                )}

                {/* API Keys Configuration */}
                <div className="space-y-4">
                  <h4 className="font-bold text-foreground text-xs uppercase flex items-center gap-1.5 border-b border-border pb-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-primary" /> Proveedores de Inteligencia de Frontera
                  </h4>

                  {/* Google Gemini */}
                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary" /> Google Gemini API Key (Principal)
                      </label>
                      <span className={`text-[8.5px] px-2 py-0.5 rounded font-bold uppercase ${
                        googleKey ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground"
                      }`}>
                        {googleKey ? "Configurada ✓" : "Requerida"}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type={showKeys.google ? "text" : "password"}
                        value={googleKey}
                        onChange={(e) => setGoogleKey(e.target.value)}
                        placeholder="AIzaSy..."
                        className="w-full bg-background border border-border px-3 py-2 pr-10 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeys(p => ({ ...p, google: !p.google }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {showKeys.google ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* OpenAI */}
                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                        <Cpu className="w-3.5 h-3.5 text-emerald-500" /> OpenAI API Key (GPT-6, O3 & Audio HD)
                      </label>
                      <span className={`text-[8.5px] px-2 py-0.5 rounded font-bold uppercase ${
                        openaiKey ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground"
                      }`}>
                        {openaiKey ? "Configurada ✓" : "Opcional"}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type={showKeys.openai ? "text" : "password"}
                        value={openaiKey}
                        onChange={(e) => setOpenaiKey(e.target.value)}
                        placeholder="sk-proj-..."
                        className="w-full bg-background border border-border px-3 py-2 pr-10 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeys(p => ({ ...p, openai: !p.openai }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {showKeys.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Anthropic Claude */}
                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-amber-500" /> Anthropic Claude API Key (Claude Sonnet 3.7 & Fable)
                      </label>
                      <span className={`text-[8.5px] px-2 py-0.5 rounded font-bold uppercase ${
                        anthropicKey ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground"
                      }`}>
                        {anthropicKey ? "Configurada ✓" : "Opcional"}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type={showKeys.anthropic ? "text" : "password"}
                        value={anthropicKey}
                        onChange={(e) => setAnthropicKey(e.target.value)}
                        placeholder="sk-ant-..."
                        className="w-full bg-background border border-border px-3 py-2 pr-10 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeys(p => ({ ...p, anthropic: !p.anthropic }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {showKeys.anthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* NVIDIA NIM */}
                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5 text-emerald-400" /> NVIDIA NIM API Key (Nemotron & DeepSeek R1)
                      </label>
                      <span className={`text-[8.5px] px-2 py-0.5 rounded font-bold uppercase ${
                        nvidiaKey ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground"
                      }`}>
                        {nvidiaKey ? "Configurada ✓" : "Opcional"}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type={showKeys.nvidia ? "text" : "password"}
                        value={nvidiaKey}
                        onChange={(e) => setNvidiaKey(e.target.value)}
                        placeholder="nvapi-..."
                        className="w-full bg-background border border-border px-3 py-2 pr-10 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeys(p => ({ ...p, nvidia: !p.nvidia }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {showKeys.nvidia ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Default Model Selector */}
                  <div className="grid md:grid-cols-2 gap-4 pt-2">
                    <div className="space-y-1.5">
                      <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                        Modelo de IA Predeterminado:
                      </label>
                      <select
                        value={defaultModel}
                        onChange={(e) => setDefaultModel(e.target.value)}
                        className="w-full bg-background border border-border p-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                      >
                        <option value="auto">🤖 Enrutador Inteligente Multi-LLM (Recomendado)</option>
                        <optgroup label="Google DeepMind">
                          <option value="gemini_38_flash">Gemini 3.8 Flash (Ultra-Velocidad Multimodal)</option>
                          <option value="gemini_38_pro">Gemini 3.8 Pro (Insignia Frontier & Razonamiento)</option>
                          <option value="gemini_2_flash">Gemini 2.0 Flash (Equilibrado)</option>
                        </optgroup>
                        <optgroup label="Anthropic">
                          <option value="claude_37_sonnet">Claude 3.7 Sonnet (Hybrid Thinking & Firmware)</option>
                          <option value="claude_fable_51">Claude Fable 5.1 (Multi-Agente de Precisión)</option>
                          <option value="claude_haiku_45">Claude Haiku 4.5 (Rápido)</option>
                        </optgroup>
                        <optgroup label="OpenAI">
                          <option value="gpt_6_astra">GPT-6 Astra (Visión Espacial 3D & CAD)</option>
                          <option value="gpt_58_pro">ChatGPT 5.8 Pro (Cognitivo Extremo)</option>
                          <option value="openai_o3_mini">o3-mini (Razonamiento Lógico & Hardware)</option>
                        </optgroup>
                        <optgroup label="xAI & NVIDIA">
                          <option value="grok_46">xAI Grok 4.6 (Física & Cargas ASCE 7)</option>
                          <option value="nvidia_nemotron_70b">NVIDIA Nemotron-70B (Math & High Speed)</option>
                        </optgroup>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                        URL Servidor FastAPI Backend:
                      </label>
                      <input
                        type="text"
                        value={backendUrl}
                        onChange={(e) => setBackendUrl(e.target.value)}
                        placeholder="http://localhost:8080"
                        className="w-full bg-background border border-border px-3 py-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                      />
                    </div>
                  </div>

                  {/* Visual Synthesis & 3D Render Engine (ChatGPT Images 2.5) */}
                  <div className="space-y-2 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                        <Palette className="w-3.5 h-3.5 text-primary" /> Motor de Generación Visual & Render 3D (ChatGPT Images 2.5)
                      </label>
                      <span className="text-[8.5px] px-2 py-0.5 rounded font-bold uppercase bg-primary/10 text-primary border border-primary/20">
                        Nueva Generación ⭐
                      </span>
                    </div>
                    <select
                      value={imageModel}
                      onChange={(e) => {
                        setImageModel(e.target.value);
                        localStorage.setItem("PROMETHEUS_IMAGE_MODEL", e.target.value);
                      }}
                      className="w-full bg-background border border-border p-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                    >
                      <optgroup label="OpenAI - ChatGPT Images 2.5 (Última Generación)">
                        <option value="gpt-image-2.5-flare">ChatGPT Images 2.5 Flare (Ultra Rápido ⭐ Recomendado)</option>
                        <option value="gpt-image-2.5-sunburst">ChatGPT Images 2.5 Sunburst (Ultra Detalle & Texturas Fotorrealistas ⭐)</option>
                        <option value="gpt-image-2.5">ChatGPT Images 2.5 (Estándar)</option>
                        <option value="dall-e-3">OpenAI DALL-E 3 (Legacy)</option>
                      </optgroup>
                      <optgroup label="Google Vertex AI / Gemini">
                        <option value="imagen-3">Google Imagen 3 (Vertex Studio)</option>
                      </optgroup>
                    </select>
                    <p className="text-[9.5px] text-muted-foreground leading-relaxed">
                      Controla los renders autónomos de taller, visualizaciones paramétricas de ensambles y bocetos del Códice con tu clave de OpenAI.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ================= TAB 2: HARDWARE & WEB SERIAL ================= */}
            {activeTab === "hardware" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                {/* Serial Status Banner */}
                <div className="p-4 rounded-xl bg-muted/20 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shrink-0">
                      <Cpu className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                        Web Serial API (Navegador)
                      </span>
                      <span className="text-foreground font-bold text-xs">
                        {hasWebSerialSupport ? "🟢 Conexión USB Directa Compatible" : "🟡 Requiere Chrome / Edge / Opera"}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={requestSerialPermissionTest}
                    className="px-3.5 py-1.5 rounded-xl bg-card hover:bg-muted border border-border text-foreground font-bold text-xs flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <Terminal className="w-3.5 h-3.5 text-primary" />
                    <span>Detectar Puerto USB</span>
                  </button>
                </div>

                {/* Serial Parameters */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <label className="text-[10px] uppercase font-bold text-foreground block">
                      Velocidad Baudios Predeterminada (Baud Rate):
                    </label>
                    <select
                      value={baudRate}
                      onChange={(e) => setBaudRate(parseInt(e.target.value, 10))}
                      className="w-full bg-background border border-border p-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                    >
                      <option value={115200}>115200 baud (Estándar ESP32 / ESP32-S3 / Pico)</option>
                      <option value={9600}>9600 baud (Arduino Uno / Nano Tradicional)</option>
                      <option value={57600}>57600 baud (Módulos GPS / LoRa)</option>
                      <option value={230400}>230400 baud (Alta Velocidad ESP-IDF)</option>
                      <option value={921600}>921600 baud (Flashing Rápido)</option>
                    </select>
                    <span className="text-[8.5px] text-muted-foreground block mt-1">
                      Alineado con el monitor UART de la suite Arduino y el Hub MCP.
                    </span>
                  </div>

                  <div className="space-y-1.5 bg-muted/10 p-3.5 rounded-xl border border-border">
                    <label className="text-[10px] uppercase font-bold text-foreground block">
                      Puerto Servidor MCP Local (Anthropic Standard):
                    </label>
                    <input
                      type="number"
                      value={mcpPort}
                      onChange={(e) => setMcpPort(parseInt(e.target.value, 10) || 8080)}
                      className="w-full bg-background border border-border px-3 py-2.5 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                    />
                    <span className="text-[8.5px] text-muted-foreground block mt-1">
                      Endpoint JSON-RPC para Claude Desktop: <code>http://localhost:{mcpPort}/mcp</code>
                    </span>
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-2 bg-muted/10 p-3.5 rounded-xl border border-border">
                  <span className="text-[10px] text-muted-foreground uppercase font-bold block mb-2">
                    Comportamiento de la Consola Serial:
                  </span>
                  
                  <label className="flex items-center gap-2 text-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={autoScrollSerial}
                      onChange={(e) => setAutoScrollSerial(e.target.checked)}
                      className="rounded border-border accent-primary"
                    />
                    <span>Auto-desplazamiento automático al recibir nuevos paquetes UART</span>
                  </label>

                  <label className="flex items-center gap-2 text-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={serialTimestamps}
                      onChange={(e) => setSerialTimestamps(e.target.checked)}
                      className="rounded border-border accent-primary"
                    />
                    <span>Incluir marcas de tiempo [HH:MM:SS.mmm] en los registros seriales</span>
                  </label>
                </div>
              </div>
            )}

            {/* ================= TAB 3: PUERTO RICO & CLIMATE ================= */}
            {activeTab === "pr_location" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                {/* 78 Municipalities Selector */}
                <div className="space-y-2 bg-muted/10 p-4 rounded-xl border border-border">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label className="text-[10px] uppercase font-bold text-foreground flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-primary" /> Municipio de Residencia / Taller en PR (78 Municipios):
                    </label>
                    <span className="text-[9px] text-muted-foreground">
                      {filteredMunicipalities.length} municipios encontrados
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={municipalityQuery}
                        onChange={(e) => setMunicipalityQuery(e.target.value)}
                        placeholder="Filtrar por pueblo o región (ej. Ponce, Caguas, Montaña)..."
                        className="w-full bg-background border border-border pl-8 pr-2.5 py-2 rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary font-mono-tech"
                      />
                    </div>

                    <select
                      value={selectedMunicipality}
                      onChange={(e) => setSelectedMunicipality(e.target.value)}
                      className="w-full bg-background border border-border p-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                    >
                      {filteredMunicipalities.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} · {m.region} ({m.hsp} HSP / {m.windMph} mph)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Live Engineering Metrics for Selected Town */}
                <div className="p-4 rounded-xl bg-card border border-border space-y-3 shadow-xs">
                  <div className="flex items-center justify-between border-b border-border pb-2">
                    <span className="text-[10.5px] text-primary uppercase font-bold tracking-wider">
                      Perfil Físico & Meteorológico: {currentMun.name} ({currentMun.region})
                    </span>
                    <span className="text-[9px] text-muted-foreground font-mono">ASCE 7-22 Compliant</span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2.5 rounded-xl bg-muted/20 border border-border">
                      <span className="text-[8.5px] text-muted-foreground uppercase block font-bold">Radiación Solar Promedio</span>
                      <span className="text-base font-bold text-amber-500">{currentMun.hsp} HSP/día</span>
                    </div>
                    <div className="p-2.5 rounded-xl bg-muted/20 border border-border">
                      <span className="text-[8.5px] text-muted-foreground uppercase block font-bold">Carga de Viento Cat 5</span>
                      <span className="text-base font-bold text-sky-500">{currentMun.windMph} mph</span>
                    </div>
                    <div className="p-2.5 rounded-xl bg-muted/20 border border-border">
                      <span className="text-[8.5px] text-muted-foreground uppercase block font-bold">Humedad Relativa</span>
                      <span className="text-base font-bold text-primary">{currentMun.humidityAvg}% HR</span>
                    </div>
                  </div>
                </div>

                {/* LUMA & Power Storage Calibration */}
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5 bg-muted/10 p-3 rounded-xl border border-border">
                    <label className="text-[9.5px] text-muted-foreground uppercase font-bold block">
                      Tarifa Eléctrica LUMA ($/kWh):
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.10"
                      max="0.60"
                      value={lumaRate}
                      onChange={(e) => setLumaRate(parseFloat(e.target.value) || 0.28)}
                      className="w-full bg-background border border-border px-3 py-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono"
                    />
                    <span className="text-[8px] text-muted-foreground">Promedio PR actual: $0.28/kWh</span>
                  </div>

                  <div className="space-y-1.5 bg-muted/10 p-3 rounded-xl border border-border">
                    <label className="text-[9.5px] text-muted-foreground uppercase font-bold block">
                      Voltaje Banco LiFePO4:
                    </label>
                    <select
                      value={systemVoltage}
                      onChange={(e) => setSystemVoltage(parseInt(e.target.value, 10) as any)}
                      className="w-full bg-background border border-border p-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                    >
                      <option value={48}>48V (Recomendado / Eficiente)</option>
                      <option value={24}>24V (Taller Mediano)</option>
                      <option value={12}>12V (Portátil / Emergencia)</option>
                    </select>
                    <span className="text-[8px] text-muted-foreground">Define calibres de cable AWG</span>
                  </div>

                  <div className="space-y-1.5 bg-muted/10 p-3 rounded-xl border border-border">
                    <label className="text-[9.5px] text-muted-foreground uppercase font-bold block">
                      Autonomía Deseada (Horas):
                    </label>
                    <select
                      value={batteryAutonomyH}
                      onChange={(e) => setBatteryAutonomyH(parseInt(e.target.value, 10))}
                      className="w-full bg-background border border-border p-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-mono-tech"
                    >
                      <option value={12}>12 Horas (Respaldo Nocturno)</option>
                      <option value={24}>24 Horas (1 Día Completo)</option>
                      <option value={48}>48 Horas (Tormenta Tropical)</option>
                      <option value={72}>72 Horas (Huracán Mayor)</option>
                    </select>
                    <span className="text-[8px] text-muted-foreground">Calcula kWh de almacenamiento</span>
                  </div>
                </div>

                {/* Sourcing in Puerto Rico Reminder */}
                <div className="p-3.5 bg-primary/5 border border-primary/20 rounded-xl text-[10px] text-muted-foreground leading-relaxed">
                  🇵🇷 <strong>Directorio de Abasto Recomendado:</strong> Tornillería de acero inoxidable 316 y anclajes estructurales en <em>Home Depot</em>, <em>National Lumber</em> o <em>Ferreterías Ace</em>. Envíos postales domésticos de microcontroladores y sensores mediante USPS desde Mouser, Digi-Key o Adafruit.
                </div>
              </div>
            )}

            {/* ================= TAB 4: VOICE & ACOUSTICS ================= */}
            {activeTab === "voice" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                <div className="p-4 rounded-xl bg-muted/10 border border-border space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <div>
                      <h4 className="font-bold text-foreground text-xs uppercase flex items-center gap-1.5">
                        <Radio className="w-3.5 h-3.5 text-emerald-500 animate-pulse" /> Motor Acústico Ultra-Humano de Prometheus
                      </h4>
                      <span className="text-[9px] text-muted-foreground">
                        {availableVoiceList.length} voces neurales detectadas ({voiceConfig?.languageMode?.toUpperCase() || "ES-LATAM"})
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        const phrases: Record<string, string> = {
                          "es-latam": "Hola creador. La voz de Prometheus está calibrada y lista para guiar tus inventos en el taller.",
                          "en-us": "Hello creator. Prometheus acoustic engine is online and calibrated for laboratory assistance.",
                          "bilingual": "Motor acústico listo. Prometheus puede procesar términos técnicos en inglés y español con fluidez natural."
                        };
                        speak(phrases[voiceConfig?.languageMode || "es-latam"] || phrases["es-latam"]);
                      }}
                      className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                    >
                      <Volume2 className="w-3.5 h-3.5" />
                      <span>Probar Audio</span>
                    </button>
                  </div>

                  {/* Provider Selector */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                      Tecnología de Síntesis Vocal:
                    </label>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => updateVoiceConfig({ provider: "openai-hd" })}
                        className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                          voiceConfig?.provider === "openai-hd"
                            ? "bg-primary/10 border-primary text-foreground shadow-xs ring-1 ring-primary/30"
                            : "bg-background border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <div className="font-bold text-xs flex items-center gap-1.5 text-primary">
                          <Sparkles className="w-3.5 h-3.5" />
                          <span>🌟 OpenAI HD (Calidad de Estudio)</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-1">
                          Voz humana con respiración natural y matices de laboratorio. Requiere API Key de OpenAI.
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => updateVoiceConfig({ provider: "browser" })}
                        className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                          voiceConfig?.provider === "browser"
                            ? "bg-primary/10 border-primary text-foreground shadow-xs ring-1 ring-primary/30"
                            : "bg-background border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <div className="font-bold text-xs flex items-center gap-1.5 text-emerald-500">
                          <Radio className="w-3.5 h-3.5" />
                          <span>🌐 Voces Neurales del Navegador (Gratuito)</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-1">
                          Procesamiento 100% local en tu dispositivo con voces Microsoft / Google Natural.
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Timbre selection if OpenAI HD */}
                  {voiceConfig?.provider === "openai-hd" && (
                    <div className="space-y-1.5 bg-background p-3.5 rounded-xl border border-primary/20">
                      <label className="text-[10px] text-primary uppercase font-bold block flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Timbre de Voz OpenAI HD:
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { id: "onyx", label: "Onyx 👨‍🔧 (Prometheus)", desc: "Masculino: Grave, técnico y autoritario" },
                          { id: "echo", label: "Echo 👨‍🔬 (Científico)", desc: "Masculino: Calmado y reflexivo" },
                          { id: "alloy", label: "Alloy 🤖 (Neutral)", desc: "Neutral: Equilibrado y directo" },
                          { id: "nova", label: "Nova 👩‍💼 (Aura)", desc: "Femenino: Cálido y empático" },
                          { id: "shimmer", label: "Shimmer 👩‍🔬 (Radiante)", desc: "Femenino: Claro y brillante" },
                          { id: "fable", label: "Fable 👨‍🏫 (Mentor)", desc: "Masculino: Rítmico de taller" },
                        ].map((v) => (
                          <button
                            key={v.id}
                            type="button"
                            onClick={() => {
                              updateVoiceConfig({ openaiVoice: v.id as any });
                              speak(`Timbre de voz ${v.label} activado.`);
                            }}
                            className={`p-2 rounded-lg border text-left cursor-pointer transition-all ${
                              voiceConfig?.openaiVoice === v.id
                                ? "bg-primary/20 border-primary text-foreground font-bold shadow-xs"
                                : "bg-card border-border text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <div className="text-xs">{v.label}</div>
                            <div className="text-[8.5px] text-muted-foreground leading-tight mt-0.5">{v.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sliders */}
                  <div className="grid md:grid-cols-2 gap-4 pt-1">
                    <div className="space-y-1.5 bg-background p-3 rounded-xl border border-border">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground uppercase font-bold">Tono (Pitch):</span>
                        <span className="text-primary font-bold">{(voiceConfig?.pitch || 1).toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min="0.8"
                        max="1.2"
                        step="0.05"
                        value={voiceConfig?.pitch || 1}
                        onChange={(e) => updateVoiceConfig({ pitch: parseFloat(e.target.value) })}
                        className="w-full accent-primary"
                      />
                    </div>

                    <div className="space-y-1.5 bg-background p-3 rounded-xl border border-border">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground uppercase font-bold">Velocidad (Rate):</span>
                        <span className="text-primary font-bold">{(voiceConfig?.rate || 1).toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="0.85"
                        max="1.3"
                        step="0.05"
                        value={voiceConfig?.rate || 1}
                        onChange={(e) => updateVoiceConfig({ rate: parseFloat(e.target.value) })}
                        className="w-full accent-primary"
                      />
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="space-y-2 pt-1 border-t border-border">
                    <label className="flex items-center gap-2 text-foreground cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={voiceConfig?.autoSpeakResponses || false}
                        onChange={(e) => updateVoiceConfig({ autoSpeakResponses: e.target.checked })}
                        className="rounded border-border accent-primary"
                      />
                      <span>Reproducir automáticamente las respuestas del asistente de laboratorio</span>
                    </label>

                    <label className="flex items-center gap-2 text-foreground cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={voiceConfig?.soundEffects !== false}
                        onChange={(e) => updateVoiceConfig({ soundEffects: e.target.checked })}
                        className="rounded border-border accent-primary"
                      />
                      <span>Efectos sonoros táctiles (chimes de conexión serial y reactor)</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ================= TAB 5: APPEARANCE ================= */}
            {activeTab === "appearance" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                <div className="p-4 rounded-xl bg-muted/10 border border-border space-y-4">
                  <h4 className="font-bold text-foreground text-xs uppercase flex items-center gap-1.5 border-b border-border pb-2">
                    <Palette className="w-3.5 h-3.5 text-primary" /> Estilo Visual & Modo de Taller
                  </h4>

                  {/* Dark / Light Theme Selector */}
                  <div className="space-y-2">
                    <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                      Tema de la Interfaz:
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleToggleTheme("dark")}
                        className={`p-4 rounded-xl border text-left cursor-pointer transition-all flex items-center justify-between ${
                          theme === "dark"
                            ? "bg-primary/10 border-primary ring-1 ring-primary/30 text-foreground"
                            : "bg-background border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Moon className="w-5 h-5 text-primary" />
                          <div>
                            <span className="font-bold block text-xs">Modo Oscuro (Dark Industrial)</span>
                            <span className="text-[9px] text-muted-foreground">Máximo contraste y protección visual</span>
                          </div>
                        </div>
                        {theme === "dark" && <CheckCircle2 className="w-4 h-4 text-primary" />}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleToggleTheme("light")}
                        className={`p-4 rounded-xl border text-left cursor-pointer transition-all flex items-center justify-between ${
                          theme === "light"
                            ? "bg-primary/10 border-primary ring-1 ring-primary/30 text-foreground"
                            : "bg-background border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Sun className="w-5 h-5 text-amber-500" />
                          <div>
                            <span className="font-bold block text-xs">Modo Claro (Clean Lab)</span>
                            <span className="text-[9px] text-muted-foreground">Fondo blanco nítido para entornos iluminados</span>
                          </div>
                        </div>
                        {theme === "light" && <CheckCircle2 className="w-4 h-4 text-primary" />}
                      </button>
                    </div>
                  </div>

                  {/* Language Selector */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                      Idioma del Laboratorio:
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => { if (lang !== "es") toggleLang(); }}
                        className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                          lang === "es"
                            ? "bg-primary/10 border-primary text-foreground font-bold"
                            : "bg-background border-border text-muted-foreground"
                        }`}
                      >
                        <span className="block text-xs">🇵🇷 Español (Puerto Rico)</span>
                        <span className="text-[8.5px] text-muted-foreground">Terminología de ingeniería y abasto local</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => { if (lang !== "en") toggleLang(); }}
                        className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                          lang === "en"
                            ? "bg-primary/10 border-primary text-foreground font-bold"
                            : "bg-background border-border text-muted-foreground"
                        }`}
                      >
                        <span className="block text-xs">🇺🇸 English (US)</span>
                        <span className="text-[8.5px] text-muted-foreground">Standard American engineering vocabulary</span>
                      </button>
                    </div>
                  </div>

                  {/* Blueprint Grid Density */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                      Cuadrícula Blueprint de Fondo:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "normal", label: "Nítida (10%)" },
                        { id: "subtle", label: "Sutil (5%)" },
                        { id: "off", label: "Desactivada" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setBlueprintOpacity(opt.id as any)}
                          className={`p-2 rounded-xl border text-center text-xs font-bold cursor-pointer transition-all ${
                            blueprintOpacity === opt.id
                              ? "bg-primary/20 border-primary text-primary"
                              : "bg-background border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= TAB 6: BACKUPS & STORAGE ================= */}
            {activeTab === "backups" && (
              <div className="space-y-5 animate-in fade-in duration-150">
                <div className="p-4 rounded-xl bg-muted/10 border border-border space-y-4">
                  <div>
                    <h4 className="font-bold text-foreground text-xs uppercase flex items-center gap-1.5">
                      <Database className="w-3.5 h-3.5 text-primary" /> Almacenamiento & Respaldos de Laboratorio
                    </h4>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Tus proyectos, prototipos CAD, inventario y notas de bitácora se guardan en la memoria local de tu navegador.
                    </p>
                  </div>

                  {/* Storage Meter */}
                  <div className="p-3.5 bg-background rounded-xl border border-border space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground font-bold flex items-center gap-1.5">
                        <HardDrive className="w-3.5 h-3.5 text-primary" /> Uso de Memoria LocalStorage:
                      </span>
                      <span className="font-mono font-bold text-foreground">
                        {Math.round(storageUsageBytes / 1024)} KB / 5,120 KB
                      </span>
                    </div>
                    <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                      <div 
                        className="bg-primary h-full transition-all duration-300"
                        style={{ width: `${Math.min(100, Math.max(3, (storageUsageBytes / (5 * 1024 * 1024)) * 100))}%` }}
                      />
                    </div>
                  </div>

                  {/* Export & Import Grid */}
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <div className="p-4 rounded-xl bg-background border border-border space-y-3 flex flex-col justify-between">
                      <div>
                        <span className="font-bold text-foreground block text-xs">Descargar Respaldo Total</span>
                        <p className="text-[9.5px] text-muted-foreground mt-1">
                          Exporta un archivo <code>.json</code> completo con tus proyectos, BOMs, inventario y llaves para archivar o transferir de equipo.
                        </p>
                      </div>
                      <button
                        onClick={handleExportBackup}
                        className="w-full py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-xs"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Exportar Respaldo (.json)</span>
                      </button>
                    </div>

                    <div className="p-4 rounded-xl bg-background border border-border space-y-3 flex flex-col justify-between">
                      <div>
                        <span className="font-bold text-foreground block text-xs">Restaurar Respaldo</span>
                        <p className="text-[9.5px] text-muted-foreground mt-1">
                          Carga un archivo <code>.json</code> de respaldo para restaurar proyectos y configuración en segundos.
                        </p>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImportBackup}
                        accept=".json"
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full py-2 bg-muted/60 hover:bg-muted border border-border text-foreground font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-xs"
                      >
                        <Upload className="w-3.5 h-3.5 text-sky-500" />
                        <span>Importar Respaldo</span>
                      </button>
                    </div>
                  </div>

                  {/* Danger Zone: Factory Reset */}
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <span className="font-bold text-red-400 block text-xs">Restablecer Valores de Fábrica</span>
                      <p className="text-[9.5px] text-red-300/80 mt-0.5">
                        Elimina todas las sesiones, inventario, claves API y proyectos almacenados en este navegador.
                      </p>
                    </div>
                    <button
                      onClick={() => setResetConfirmOpen(true)}
                      className="px-3.5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition-all cursor-pointer shrink-0 flex items-center gap-1.5 shadow-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Reset General</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

          </main>
        </div>

        {/* Modal Bottom Footer Bar */}
        <div className="p-3.5 sm:p-4 border-t border-border bg-muted/20 flex items-center justify-between text-[10px] text-muted-foreground shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Prometheus Lab AI · v2.6 Pro (Puerto Rico Edition)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-card hover:bg-muted border border-border rounded-xl text-foreground font-bold transition-all cursor-pointer shadow-xs"
            >
              Cerrar
            </button>
            <button
              onClick={handleSaveAll}
              className="px-4 py-1.5 bg-primary text-primary-foreground font-bold rounded-xl shadow-xs hover:bg-primary/90 transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Save className="w-3 h-3" />
              <span>Guardar Ajustes</span>
            </button>
          </div>
        </div>

      </div>

      {/* Confirmation Dialog for Factory Reset */}
      <ConfirmDialog
        open={resetConfirmOpen}
        title="¿Restablecer Plataforma a Valores de Fábrica?"
        description="Esta acción eliminará todos los proyectos, inventario, notas y claves API guardadas localmente en este navegador. Te sugerimos exportar un archivo de respaldo antes de continuar."
        confirmLabel="Sí, Borrar Todo"
        cancelLabel="Cancelar"
        onConfirm={handleFactoryReset}
        onCancel={() => setResetConfirmOpen(false)}
      />
    </div>,
    document.body
  );
};
